import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { WeeklyLeaderboard } from "./-components/weekly-leaderboard";
import { Link } from "@tanstack/react-router";
import ChampionshipButton from "@/components/ChampionshipButton";
import { ItemChampionshipDialog } from "@/components/ItemChampionshipDialog";
import { DailyTrophyBonusButton } from "@/components/DailyTrophyBonusButton";
import { useLeaderboardList, useStats, useTheme } from "@/lib/hooks/useApi";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import type { CustomLeaderboard } from "./-components/types";
import AnimatedLoading from "@/components/AnimatedLoading";
import { useTranslation } from "@/lib/hooks/useTranslation";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { InfoIcon } from "lucide-react";
import { toast } from "sonner";
import { DefaultButton } from "@/components/DefaultButton";
import { ShinyWavyText } from "@/components/ShinyWavyText";
import { cn, pixelCornerClipPath } from "@/lib/utils";

dayjs.extend(duration);
dayjs.extend(utc);

export const Route = createFileRoute("/(game)/leaderboard/")({
  component: RouteComponent,
});

const PRO_TIER = 1;
const UNVERIFIED_TOAST_ID = "toast:leaderboard-unverified";

function RouteComponent() {
  const navigate = useNavigate();
  const { user } = usePrivy();
  const { t } = useTranslation();
  const walletAddress = user?.wallet?.address ?? "";
  const { data: theme } = useTheme();
  const { data: stats } = useStats(walletAddress);
  const gameWalletAddress = stats?.embeddedWallet;
  const [weeklyIndex, setWeeklyIndex] = useState<number | undefined>(undefined);
  const weeklyQuery = useLeaderboardList(100, PRO_TIER, weeklyIndex);

  const isLiveWeekly = weeklyQuery.data?.isLive !== false;
  const isChampionshipActive = theme?.championship.status.isActive ?? false;
  const proTimestamp = isChampionshipActive
    ? theme?.leaderboard.timestamp
    : undefined;
  const now = Date.now() / 1000;
  const isChampionshipOpen =
    isLiveWeekly && (proTimestamp ? now >= proTimestamp.open : false);
  const isChampionshipStarted =
    isLiveWeekly && (proTimestamp ? now >= proTimestamp.started : false);
  const isChampionshipEnded =
    isLiveWeekly && (proTimestamp ? now > proTimestamp.ended : false);
  const shouldRenderChampionshipLogo =
    isChampionshipOpen && !isChampionshipStarted && !isChampionshipEnded;
  const shouldShowChampionshipButton =
    isChampionshipOpen && !isChampionshipStarted;
  const shouldShowChampionshipAsLinkButton =
    shouldShowChampionshipButton && isChampionshipStarted;

  useEffect(() => {
    if (!walletAddress || stats?.isVerified !== false) {
      toast.dismiss(UNVERIFIED_TOAST_ID);
      return;
    }

    toast.warning("YOUR ACCOUNT IS UNVERIFIED", {
      id: UNVERIFIED_TOAST_ID,
      description:
        "Link Twitter/X and at least 1 other social to get verified and receive weekly rewards.",
      duration: Infinity,
      closeButton: false,
      dismissible: false,
      className: "flex flex-col",
      action: (
        <div className="flex w-full justify-end space-x-1">
          <DefaultButton
            variant="white"
            className="uppercase text-black"
            onClick={() => toast.dismiss(UNVERIFIED_TOAST_ID)}
          >
            Close
          </DefaultButton>
          <DefaultButton
            className="uppercase"
            onClick={() => {
              toast.dismiss(UNVERIFIED_TOAST_ID);
              navigate({ to: "/profile", search: { socialLink: true } });
            }}
          >
            Verify My Account
          </DefaultButton>
        </div>
      ),
    });
  }, [stats?.isVerified, walletAddress, navigate]);

  const weeklyRows: CustomLeaderboard[] = useMemo(() => {
    const lb = weeklyQuery.data?.leaderboard ?? [];
    return lb.map((item, index) => ({
      id: index + 1,
      position: index + 1,
      isCurrentUser: item.userPublicKey === gameWalletAddress,
      ...item,
    }));
  }, [weeklyQuery.data?.leaderboard, gameWalletAddress]);

  return (
    <div
      className="flex-1 min-h-0 flex flex-col relative"
      style={{
        backgroundImage:
          "linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('/assets/bg-global-4.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        imageRendering: "pixelated",
      }}
    >
      <FloatingLeftButton
        onClick={() => navigate({ to: "/home" })}
        className="absolute left-0 top-4 z-20"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />
      {/* <div className="absolute right-2 top-5 z-20"> */}
      {/*   <WeeklyIndexSelect */}
      {/*     indexes={weeklyQuery.data?.availableIndexes ?? []} */}
      {/*     current={weeklyIndex ?? weeklyQuery.data?.index ?? null} */}
      {/*     isLive={isLiveWeekly} */}
      {/*     onChange={setWeeklyIndex} */}
      {/*   /> */}
      {/* </div> */}

      {/* Title and Timer — hidden while loading */}
      {!weeklyQuery.isLoading && (
        <>
          <div className="absolute top-2 right-1 w-16 scale-80 flex flex-col items-center space-y-2 sm:space-y-3 z-10">
            {shouldShowChampionshipButton && (
              <ItemChampionshipDialog>
                <ChampionshipButton isIconOnLeaderboard={true} />
              </ItemChampionshipDialog>
            )}

            {shouldShowChampionshipAsLinkButton && (
              <Link to="/championship">
                <ChampionshipButton isIconOnLeaderboard={true} />
              </Link>
            )}

            {!!user && <DailyTrophyBonusButton source="leaderboard" />}
          </div>

          <div className="mt-16 px-2 flex w-full items-center justify-center">
            <div className="relative flex flex-col items-center justify-center gap-1">
              {shouldRenderChampionshipLogo && (
                <img
                  src={theme?.championship.logo ?? ""}
                  alt="winter-cup-logo"
                  className="h-20"
                />
              )}
              <div className="flex items-center justify-center gap-2">
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-white uppercase text-center">
                    {!isChampionshipEnded &&
                    isChampionshipOpen &&
                    !isChampionshipStarted
                      ? t("leaderboard.titleQualifier")
                      : t("leaderboard.titleWeekly")}
                  </h2>
                  {shouldRenderChampionshipLogo && (
                    <div className="text-white/90">
                      {[
                        theme?.leaderboard.campaignDescriptionLine1,
                        theme?.leaderboard.campaignDescriptionLine2,
                      ]
                        .filter(Boolean)
                        .map((line, i) => {
                          const [before, after] = line!.split("{prize}");
                          return (
                            <p key={i}>
                              {before && (
                                <span className="me-1">{before.trim()}</span>
                              )}
                              <ShinyWavyText
                                text={theme!.proLeaderboard.campaignPrize ?? ""}
                                amplitude={-2.25}
                                className="text-xs inline-flex items-baseline"
                              />
                              {after && (
                                <span className="ms-1">{after.trim()}</span>
                              )}
                            </p>
                          );
                        })}
                    </div>
                  )}
                </div>
                {!shouldRenderChampionshipLogo && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-black/20 hover:bg-black/30 transition-colors align-middle mt-1"
                        aria-label="Verification info"
                      >
                        <InfoIcon className="size-4 text-white" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="max-w-xs text-sm text-left">
                      <p>
                        Get your verified badge by linking a minimum of 2/3
                        social accounts to be eligible for prize distribution.
                      </p>
                    </PopoverContent>
                  </Popover>
                )}
              </div>

              <div className="flex items-center justify-center gap-2 black-container h-8 px-1">
                {isLiveWeekly ? (
                  <>
                    <img
                      src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
                      className="w-3.5"
                      alt={t("leaderboard.timerAlt")}
                    />
                    <p className="font-extrabold text-white/80 text-base tracking-wider pl-1">
                      {(() => {
                        const target = theme?.leaderboard.resetDate ?? 0;
                        const nowTs = dayjs().unix();
                        const dur = dayjs.duration(target - nowTs, "seconds");
                        const days = Math.floor(dur.asDays());
                        const hours = dur.hours();
                        const minutes = dur.minutes();
                        return `${days}d ${hours}h ${minutes}m`;
                      })()}
                    </p>
                  </>
                ) : (
                  <p className="mx-1 font-extrabold text-green-400/80 text-xs tracking-wider uppercase">
                    Prize Distributed
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Leaderboard content */}
      <div className="flex-1 min-h-0 flex flex-col relative">
        {weeklyQuery.isLoading ? (
          <AnimatedLoading />
        ) : (
          <WeeklyLeaderboard
            rows={weeklyRows}
            error={weeklyQuery.error}
            refetch={weeklyQuery.refetch}
            stats={stats}
            walletAddress={walletAddress}
            campaignTrophyIcon={
              shouldRenderChampionshipLogo
                ? theme?.leaderboard.campaignTrophyIcon
                : undefined
            }
            prizeList={
              isLiveWeekly ? theme?.leaderboard.weeklyPrize : undefined
            }
          />
        )}
      </div>
    </div>
  );
}

function WeeklyIndexSelect({
  indexes,
  current,
  isLive,
  onChange,
}: {
  indexes: number[];
  current: number | null;
  isLive: boolean;
  onChange: (index: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (current === null || indexes.length === 0) return null;

  const currentLabel = `Week ${current}`;

  return (
    <div ref={ref} className="relative ml-auto">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 bg-[#005CBC] text-xs text-white px-2 py-1 cursor-pointer"
        style={{ clipPath: pixelCornerClipPath(1.5) }}
      >
        {currentLabel}
        <svg
          className={cn("w-3 h-3 transition-transform", open && "rotate-180")}
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M6 9L12 15L18 9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-1 bg-[#005CBC] shadow-lg overflow-hidden max-h-48 overflow-y-auto"
          style={{ clipPath: pixelCornerClipPath(1.5) }}
        >
          {[...indexes].reverse().map((idx, i) => {
            const isLatest = i === 0;
            const label = isLatest ? `Week ${idx} (Live)` : `Week ${idx}`;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  onChange(idx);
                  setOpen(false);
                }}
                className={cn(
                  "w-full px-3 py-1.5 text-xs text-left whitespace-nowrap hover:bg-white/10",
                  idx === current ? "text-white font-bold" : "text-white/50",
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
